import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import Icon from "@mdi/react";
import {
    mdiAccountGroup,
    mdiAccountCircleOutline,
    mdiChevronRight,
    mdiWalletOutline,
    mdiCreditCardPlusOutline,
    mdiCreditCardMinusOutline,
    mdiClose,
} from "@mdi/js";

import { bookingApi } from "../../api/booking";
import { formatCurrencyFromMinor } from "../../utils/currency";
import AppHeader from "../../components/AppHeader";
import BaseModal from "../../components/BaseModal";
import FolderTabs from "../../components/FolderTabs";

import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./BookingStudentsPage.css";

// "Friday, 15 May 2026" — readable, unambiguous, locale-independent
const formatDate = iso => {
    if (!iso) return "Not set";
    const dt = DateTime.fromISO(iso);
    if (!dt.isValid) return "Not set";
    return dt.toFormat("EEEE, d LLLL yyyy");
};

const initialsFromName = name => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() || "").join("") || "?";
};

const matchesStudent = (booking, student) => {
    if (!student) return false;
    return Boolean(booking.studentProfileId) && booking.studentProfileId === student.studentProfileId;
};

function BookingStudentDetailModal({ student, isOpen, onClose }) {
    const [allBookings, setAllBookings] = useState([]);
    const [credit, setCredit] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || !student) {
            setAllBookings([]);
            setCredit(null);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            try {
                const [bookings, creditRes] = await Promise.all([
                    bookingApi.getInstructorBookings(),
                    student.studentProfileId
                        ? bookingApi.getCreditBalance(student.studentProfileId).catch(() => null)
                        : Promise.resolve(null),
                ]);
                if (cancelled) return;
                setAllBookings(bookings.filter(b => matchesStudent(b, student)));
                setCredit(
                    creditRes
                        ? {
                              balance: creditRes.creditBalance,
                              currency: creditRes.currency,
                              entries: creditRes.entries || [],
                          }
                        : null
                );
            } catch (err) {
                if (!cancelled) toast.error("Failed to load student bookings.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [isOpen, student]);

    if (!isOpen || !student) return null;

    const now = new Date();
    const upcoming = allBookings.filter(b => new Date(b.bookingDate) >= now);
    const past = allBookings.filter(b => new Date(b.bookingDate) < now);
    const withPricing = allBookings.filter(b => b.pricing);

    const renderBookingRow = b => (
        <div key={b.id} className="bsp-booking-row">
            <span className="bsp-booking-date">{formatDate(b.bookingDate)}</span>
            <span className="bsp-booking-time">
                {b.timeSlot?.startTime || ""}
                {b.timeSlot?.endTime ? ` to ${b.timeSlot.endTime}` : ""}
            </span>
            <span className={`bsp-status-badge bsp-status-${b.status || "pending"}`}>
                {b.status || "pending"}
            </span>
        </div>
    );

    const renderPaymentRow = b => (
        <div key={b.id} className="bsp-booking-row">
            <span className="bsp-booking-date">{formatDate(b.bookingDate)}</span>
            <span className="bsp-booking-time">
                {b.timeSlot?.startTime || ""}
                {b.timeSlot?.endTime ? ` to ${b.timeSlot.endTime}` : ""}
            </span>
            <span className="bsp-booking-amount">
                {b.pricing?.amount
                    ? formatCurrencyFromMinor(b.pricing.amount, b.pricing.currency)
                    : "Not set"}
            </span>
            <span
                className={`bsp-status-badge bsp-payment-${
                    b.pricing?.paymentStatus || "pending"
                }`}
            >
                {b.pricing?.paymentStatus || "pending"}
            </span>
        </div>
    );

    const creditCurrency = (credit?.currency || "AUD").toUpperCase();
    const creditEntries = credit?.entries || [];

    const renderCreditsTab = () => {
        if (creditEntries.length === 0) {
            return <p className="bsp-empty-state">No credit activity yet.</p>;
        }
        return (
            <div className="bsp-credit-ledger">
                <div className="bsp-credit-balance-row">
                    <span className="bsp-credit-balance-label">Current balance</span>
                    <span className="bsp-credit-balance-value">
                        {formatCurrencyFromMinor(credit.balance, creditCurrency)}
                    </span>
                </div>
                <div className="bsp-credit-entries">
                    {creditEntries.map(entry => {
                        const isGrant = entry.type === "grant";
                        return (
                            <div key={entry.id} className="bsp-credit-entry">
                                <Icon
                                    path={isGrant ? mdiCreditCardPlusOutline : mdiCreditCardMinusOutline}
                                    size={0.8}
                                    color="var(--dl-color-purple-default)"
                                />
                                <span className="bsp-credit-entry-label">
                                    {isGrant ? "Credit issued" : "Credit applied"}
                                </span>
                                <span className="bsp-credit-entry-date">{formatDate(entry.createdAt)}</span>
                                <span
                                    className={`bsp-credit-entry-amount ${
                                        isGrant ? "bsp-credit-positive" : "bsp-credit-negative"
                                    }`}
                                >
                                    {isGrant ? "+" : "-"}
                                    {formatCurrencyFromMinor(entry.amount, creditCurrency)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const tabs = [
        {
            label: "Upcoming",
            badge: upcoming.length,
            content: (
                <div className="bsp-modal-tab-content">
                    {upcoming.length === 0 ? (
                        <p className="bsp-empty-state">No upcoming bookings.</p>
                    ) : (
                        upcoming.map(renderBookingRow)
                    )}
                </div>
            ),
        },
        {
            label: "Past",
            badge: past.length,
            content: (
                <div className="bsp-modal-tab-content">
                    {past.length === 0 ? (
                        <p className="bsp-empty-state">No past bookings.</p>
                    ) : (
                        past.map(renderBookingRow)
                    )}
                </div>
            ),
        },
        {
            label: "Payments",
            badge: withPricing.length,
            content: (
                <div className="bsp-modal-tab-content">
                    {withPricing.length === 0 ? (
                        <p className="bsp-empty-state">No payment records.</p>
                    ) : (
                        withPricing.map(renderPaymentRow)
                    )}
                </div>
            ),
        },
        {
            label: "Credits",
            badge: creditEntries.length || undefined,
            content: <div className="bsp-modal-tab-content">{renderCreditsTab()}</div>,
        },
    ];

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={student.name || "Student Bookings"}
            icon={mdiAccountCircleOutline}
            maxWidth={640}
            footer={
                <button className="base-modal-cancel-btn" onClick={onClose}>
                    Close
                </button>
            }
        >
            {isLoading ? (
                <p className="bsp-loading">Loading bookings...</p>
            ) : (
                <FolderTabs tabs={tabs} defaultTab={0} />
            )}
        </BaseModal>
    );
}

const BookingStudentsPage = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [creditBalances, setCreditBalances] = useState({});
    const [hasCreditFilter, setHasCreditFilter] = useState(false);
    const [search, setSearch] = useState("");

    const loadStudents = useCallback(async () => {
        setIsLoading(true);
        try {
            const list = await bookingApi.getInstructorStudents();
            setStudents(list);
            // Fetch credit balances for non-guest students in parallel
            const nonGuest = list.filter(s => s.studentProfileId && !s.isGuest);
            if (nonGuest.length > 0) {
                const results = await Promise.all(
                    nonGuest.map(s =>
                        bookingApi.getCreditBalance(s.studentProfileId)
                            .then(data => ({ id: s.studentProfileId, data }))
                            .catch(() => ({ id: s.studentProfileId, data: { creditBalance: 0, currency: 'AUD' } }))
                    )
                );
                const balanceMap = {};
                results.forEach(({ id, data }) => {
                    balanceMap[id] = { balance: data.creditBalance, currency: data.currency };
                });
                setCreditBalances(balanceMap);
            }
        } catch (err) {
            toast.error("Failed to load students. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStudents();
    }, [loadStudents]);

    const handleRowClick = student => {
        setSelectedStudent(student);
        setShowModal(true);
    };

    const creditCount = Object.values(creditBalances).filter(c => c?.balance > 0).length;

    const visibleStudents = students
        .filter(s => !hasCreditFilter || (creditBalances[s.studentProfileId]?.balance > 0))
        .filter(s => {
            if (!search) return true;
            const q = search.toLowerCase();
            return (
                s.name?.toLowerCase().includes(q) ||
                s.email?.toLowerCase().includes(q) ||
                s.phone?.toLowerCase().includes(q)
            );
        });

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedStudent(null);
    };

    return (
        <div className="dashboard-container org-console booking-students-page">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card" style={{ padding: "12px 20px" }}>
                        <div className="sub-portal-header-title-row">
                            <button
                                className="back-nav-btn"
                                onClick={() => navigate("/instructor-dashboard")}
                            >
                                ← Bookings
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon
                                    path={mdiAccountGroup}
                                    size={1}
                                    color="var(--dl-color-purple-default)"
                                    style={{ marginRight: 8, verticalAlign: "middle" }}
                                />
                                My Students
                            </h1>
                        </div>
                        <p className="sub-portal-header-subtitle">
                            All students who have booked private lessons with you.
                        </p>
                    </div>

                    <div className="content-card bsp-roster-card">
                        <div className="bsp-toolbar">
                            <button
                                className={`bsp-filter-btn${hasCreditFilter ? ' active' : ''}`}
                                onClick={() => setHasCreditFilter(f => !f)}
                            >
                                Has credit
                                {creditCount > 0 && (
                                    <span className="bsp-filter-count">{creditCount}</span>
                                )}
                            </button>
                            <div className="bsp-search-wrap">
                                <input
                                    className="bsp-search"
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                                {search && (
                                    <button
                                        className="bsp-search-clear"
                                        onClick={() => setSearch("")}
                                        aria-label="Clear search"
                                    >
                                        <Icon path={mdiClose} size={0.7} />
                                    </button>
                                )}
                            </div>
                        </div>
                        {isLoading ? (
                            <p className="bsp-loading">Loading students...</p>
                        ) : students.length === 0 ? (
                            <div className="bsp-empty-roster">
                                <Icon
                                    path={mdiAccountGroup}
                                    size={2}
                                    color="var(--dl-color-text-muted)"
                                />
                                <p>
                                    No students yet. Students will appear here once you
                                    have booking history.
                                </p>
                            </div>
                        ) : visibleStudents.length === 0 ? (
                            <div className="bsp-empty-roster">
                                <Icon
                                    path={mdiAccountGroup}
                                    size={2}
                                    color="var(--dl-color-text-muted)"
                                />
                                <p>No students match your search.</p>
                            </div>
                        ) : (
                            <div className="bsp-students-list">
                                {visibleStudents
                                    .map((student, idx) => {
                                    const creditInfo = creditBalances[student.studentProfileId];
                                    const lastBooking = formatDate(student.lastBookingDate);
                                    const nextBooking = student.nextUpcomingBookingDate
                                        ? formatDate(student.nextUpcomingBookingDate)
                                        : null;
                                    const metaParts = [];
                                    if (student.email) metaParts.push(student.email);
                                    if (student.phone) metaParts.push(student.phone);

                                    return (
                                        <div
                                            key={student.studentProfileId || `guest-${idx}`}
                                            className="bsp-students-row"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleRowClick(student)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    handleRowClick(student);
                                                }
                                            }}
                                        >
                                            <div className="bsp-students-avatar" aria-hidden="true">
                                                {initialsFromName(student.name)}
                                            </div>

                                            <div className="bsp-students-info">
                                                <div className="bsp-students-name-row">
                                                    <span className="bsp-students-name">
                                                        {student.name}
                                                    </span>
                                                    {student.isGuest && (
                                                        <span className="bsp-guest-badge">
                                                            Guest
                                                        </span>
                                                    )}
                                                </div>
                                                {metaParts.length > 0 && (
                                                    <div className="bsp-students-meta">
                                                        {metaParts.join(" · ")}
                                                    </div>
                                                )}
                                                {creditInfo && creditInfo.balance > 0 && (
                                                    <div className="bsp-credit-badge">
                                                        <Icon path={mdiWalletOutline} size={0.65} />
                                                        {formatCurrencyFromMinor(creditInfo.balance, creditInfo.currency)} credit
                                                    </div>
                                                )}
                                            </div>

                                            <div className="bsp-students-dates-col">
                                                <div className="bsp-students-date-block">
                                                    <span className="bsp-students-date-label">Last</span>
                                                    <span className="bsp-students-date-value">{lastBooking}</span>
                                                </div>
                                                {nextBooking && (
                                                    <div className="bsp-students-date-block">
                                                        <span className="bsp-students-date-label">Next</span>
                                                        <span className="bsp-students-date-value">{nextBooking}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <Icon
                                                path={mdiChevronRight}
                                                size={1}
                                                color="var(--dl-color-text-muted)"
                                                className="bsp-students-chevron"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <BookingStudentDetailModal
                student={selectedStudent}
                isOpen={showModal}
                onClose={handleCloseModal}
            />
        </div>
    );
};

export default BookingStudentsPage;
